import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface OfferItem {
  title: string;
  description: string;
  value: number;
  icon: string;
}

interface OfferItemRowProps {
  item: OfferItem;
  delay: number;
}

function OfferItemRow({ item, delay }: OfferItemRowProps) {
  const { elementRef } = useScrollAnimation({ delay });
  return (
    <div
      ref={elementRef}
      className="animate-slide-right flex items-start gap-4 p-4 rounded-xl transition-all duration-300 hover:translate-x-1"
      style={{ background: '#ffffff', border: '1px solid #ebebeb' }}
    >
      <div
        className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0"
        style={{ background: '#B8860B18', border: '1px solid #B8860B33' }}
      >
        <i className={`${item.icon} text-lg`} style={{ color: '#B8860B' }}></i>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
        <p className="text-xs sm:text-sm" style={{ color: '#888888' }}>{item.description}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-lg sm:text-xl font-black" style={{ color: '#B8860B' }}>
          ${item.value}
        </p>
        <p className="text-xs" style={{ color: '#aaaaaa' }}>value</p>
      </div>
    </div>
  );
}

function CountdownTimer() {
  const getTimeLeft = () => {
    const stored = localStorage.getItem('offer_deadline');
    let deadline: number;
    if (stored) {
      deadline = parseInt(stored, 10);
    } else {
      deadline = Date.now() + 48 * 60 * 60 * 1000;
      localStorage.setItem('offer_deadline', String(deadline));
    }
    const diff = Math.max(0, deadline - Date.now());
    return {
      hours: Math.floor(diff / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
    };
  };

  const [timeLeft, setTimeLeft] = useState(getTimeLeft);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div
      className="rounded-2xl p-6 sm:p-8 mb-10 text-center"
      style={{
        background: 'linear-gradient(135deg, #1a0a00 0%, #2d1500 100%)',
        border: '2px solid #B8860B',
        boxShadow: '0 8px 40px rgba(184,134,11,0.18)',
      }}
    >
      <div className="flex items-center justify-center gap-2 mb-3">
        <i className="ri-alarm-warning-fill text-xl" style={{ color: '#F5D76E' }}></i>
        <p className="text-xs font-bold tracking-[0.3em] uppercase" style={{ color: '#F5D76E' }}>
          Limited-Time Offer Expires In
        </p>
        <i className="ri-alarm-warning-fill text-xl" style={{ color: '#F5D76E' }}></i>
      </div>

      <div className="flex items-center justify-center gap-3 sm:gap-5 mb-3">
        {[
          { label: 'Hours', value: pad(timeLeft.hours) },
          { label: 'Minutes', value: pad(timeLeft.minutes) },
          { label: 'Seconds', value: pad(timeLeft.seconds) },
        ].map((unit, i) => (
          <div key={unit.label} className="flex items-center gap-3 sm:gap-5">
            <div className="flex flex-col items-center">
              <div
                className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center rounded-xl"
                style={{ background: 'rgba(184,134,11,0.15)', border: '1.5px solid #B8860B' }}
              >
                <span className="text-3xl sm:text-4xl font-black tabular-nums" style={{ color: '#F5D76E' }}>
                  {unit.value}
                </span>
              </div>
              <span className="text-xs mt-1.5 font-semibold tracking-widest uppercase" style={{ color: '#aaaaaa' }}>
                {unit.label}
              </span>
            </div>
            {i < 2 && (
              <span className="text-3xl sm:text-4xl font-black mb-5" style={{ color: '#B8860B' }}>:</span>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs sm:text-sm font-medium" style={{ color: '#cccccc' }}>
        🔥 When the timer hits zero, this offer price goes up. Lock in your rate now.
      </p>
    </div>
  );
}

export default function ValueStack() {
  const { elementRef: headerRef } = useScrollAnimation();
  const { elementRef: timerRef } = useScrollAnimation({ delay: 100 });
  const { elementRef: cardRef } = useScrollAnimation({ delay: 200 });

  const offerItems: OfferItem[] = [
    {
      title: 'Personalized Training Program',
      description: 'Custom built for your body, schedule, and goals',
      value: 500,
      icon: 'ri-dumbbell-line',
    },
    {
      title: 'Complete Nutrition Blueprint',
      description: 'Macro planning and meal strategies that fit your lifestyle',
      value: 400,
      icon: 'ri-restaurant-line',
    },
    {
      title: 'Weekly 1:1 Check-Ins',
      description: 'Direct accountability and program adjustments',
      value: 600,
      icon: 'ri-calendar-check-line',
    },
    {
      title: 'Form Review & Feedback',
      description: 'Video analysis to prevent injury and maximize gains',
      value: 300,
      icon: 'ri-video-line',
    },
    {
      title: 'Performance Tracking Dashboard',
      description: 'See your progress in real time with data-driven insights',
      value: 250,
      icon: 'ri-line-chart-line',
    },
    {
      title: 'Mindset & Discipline Training',
      description: 'Mental frameworks used by elite performers',
      value: 400,
      icon: 'ri-brain-line',
    },
    {
      title: 'Private Community Access',
      description: 'Connect with other high-level people on the same path',
      value: 200,
      icon: 'ri-team-line',
    },
    {
      title: 'Direct Message Support',
      description: 'Get answers fast when you need guidance',
      value: 350,
      icon: 'ri-message-3-line',
    },
  ];

  const totalValue = offerItems.reduce((sum, item) => sum + item.value, 0);
  const actualPrice = 597;
  const savings = totalValue - actualPrice;

  return (
    <section className="py-20 sm:py-28" style={{ background: '#ffffff' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div ref={headerRef} className="animate-fade-up text-center mb-12 sm:mb-16">
          <p className="text-xs font-bold tracking-[0.35em] uppercase mb-3" style={{ color: '#B8860B' }}>
            The Complete Package
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-5 leading-tight text-gray-900">
            Everything You Get When You Join
          </h2>
          <p className="text-sm sm:text-base max-w-2xl mx-auto" style={{ color: '#666666' }}>
            This is not just a training program. This is a complete transformation system built for people who refuse to settle.
          </p>
          <div className="w-16 h-1 mx-auto rounded-full mt-6" style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)' }} />
        </div>

        {/* Countdown Timer */}
        <div ref={timerRef} className="animate-scale-up">
          <CountdownTimer />
        </div>

        {/* Value Stack Card */}
        <div
          ref={cardRef}
          className="animate-fade-up rounded-2xl p-8 sm:p-10 lg:p-12"
          style={{
            background: '#fafafa',
            border: '2px solid #f0e0b0',
            boxShadow: '0 20px 60px rgba(184,134,11,0.10)',
          }}
        >
          {/* Offer Items */}
          <div className="space-y-4 mb-8">
            {offerItems.map((item, idx) => (
              <OfferItemRow key={idx} item={item} delay={idx * 50} />
            ))}
          </div>

          {/* Divider */}
          <div className="w-full h-px mb-8" style={{ background: 'linear-gradient(90deg, transparent, #B8860B, transparent)' }} />

          {/* Total Value Breakdown */}
          <div className="space-y-3 mb-8">
            <div className="flex items-center justify-between text-lg sm:text-xl">
              <span className="font-bold" style={{ color: '#555555' }}>Total Value:</span>
              <span className="font-black line-through" style={{ color: '#aaaaaa' }}>
                ${totalValue.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between text-2xl sm:text-3xl lg:text-4xl">
              <span className="font-black text-gray-900">You Pay:</span>
              <span className="font-black" style={{ color: '#B8860B' }}>
                ${actualPrice}
                <span className="text-base sm:text-lg font-medium" style={{ color: '#888888' }}>/month</span>
              </span>
            </div>
            <div className="text-center pt-4">
              <p className="text-sm sm:text-base font-bold" style={{ color: '#B8860B' }}>
                You Save ${savings.toLocaleString()} Every Single Month
              </p>
            </div>
          </div>

          {/* Scarcity Note */}
          <div
            className="rounded-xl p-4 mb-6 text-center"
            style={{ background: '#C8102E0f', border: '1px solid #C8102E33' }}
          >
            <div className="flex items-center justify-center gap-2 mb-1">
              <i className="ri-alarm-warning-line text-lg" style={{ color: '#C8102E' }}></i>
              <p className="text-sm font-bold" style={{ color: '#C8102E' }}>LIMITED AVAILABILITY</p>
            </div>
            <p className="text-xs" style={{ color: '#555555' }}>
              Only 5 spots available this month. Once they are gone, the next opening is not until next month.
            </p>
          </div>

          {/* CTA Button */}
          <a
            href="#booking"
            className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-base transition-all duration-300 cursor-pointer whitespace-nowrap hover:opacity-90 hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(90deg, #B8860B, #F5D76E)',
              color: '#000000',
              boxShadow: '0 8px 24px rgba(184,134,11,0.25)',
            }}
          >
            <i className="ri-rocket-line text-xl"></i>
            Claim Your Spot Now
          </a>

          {/* Trust Badge */}
          <p className="text-center text-xs mt-4" style={{ color: '#888888' }}>
            <i className="ri-shield-check-line mr-1" style={{ color: '#B8860B' }}></i>
            90-Day Results Guarantee • No Long-Term Contracts
          </p>
        </div>

        {/* Bottom Note */}
        <p className="text-center text-sm mt-8" style={{ color: '#888888' }}>
          This is the exact system that has transformed over 500 lives. The question is: are you next?
        </p>
      </div>
    </section>
  );
}
